/**
 * rsaEncryptAesKey.js
 *
 * 功能：
 * 1. 随机生成 16 字节 AES Key
 * 2. 使用 RSA-OAEP（SHA-1）公钥加密
 * 3. 输出 Base64
 *
 * 对齐 Python：
 * cipher_rsa = PKCS1_OAEP.new(rsa_public_key)
 * encrypted_aes_key = cipher_rsa.encrypt(aes_key)
 */

import { KJUR, hextob64, KEYUTIL } from 'jsrsasign'
import { Base64 } from 'js-base64'
// CDN 模式请删除上面 import

/* ================= 工具函数 ================= */

/** 规范化 PEM（去除缩进/多余空白） */
function normalizePem(pem) {
  return pem
    .trim()
    .replace(/\r\n/g, '\n')
    .split('\n')
    .map((line) => line.trim())
    .join('\n')
}

/** 生成随机 AES Key（16字节） */
function generateRandomAesKey(length = 16) {
  const bytes = new Uint8Array(length)
  if (window.crypto && window.crypto.getRandomValues) {
    window.crypto.getRandomValues(bytes)
  } else {
    for (let i = 0; i < bytes.length; i++) {
      bytes[i] = Math.floor(Math.random() * 256)
    }
  }
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
}

/* ================= 核心加密 ================= */

/**
 * RSA-OAEP 加密 AES Key
 * @param {string} publicKeyPem
 * @returns {string} Base64
 */
function rsaOaepEncrypt(aesKey, publicKeyPem) {
  const keyObj = KEYUTIL.getKey(normalizePem(publicKeyPem))
  const encryptedHex = KJUR.crypto.Cipher.encrypt(
    aesKey,
    keyObj,
    'RSAOAEP',
    { hash: 'sha1' } // ⚠️ 必须与后端一致
  )
  return hextob64(encryptedHex)
}

/* ================= 对外方法（你实际用这个） ================= */

/**
 * 一步完成：
 * - 生成 AES Key
 * - RSA-OAEP 加密
 *
 * @param {string} publicKeyPem
 * @returns {{
 *   aesKeyBytes: Uint8Array,
 *   aesKeyHex: string,
 *   encryptedAesKey: string
 * }}
 */
export function generateEncryptedAesKey(publicKeyPem) {
  if (!publicKeyPem) {
    throw new Error('publicKeyPem is required')
  }

  const aesKey = generateRandomAesKey(16)
  const encryptedAesKey = rsaOaepEncrypt(aesKey, publicKeyPem)

  return {
    aesKey, // 前端 AES 用
    encryptedAesKey, // Base64 → 传后端
  }
}

/**
 *
 * @returns public key
 * -----BEGIN PUBLIC KEY-----
 * MIIBIjANBgkqhkiG9w0BAQEFAAOCAQ8AMIIBCgKCAQEA6NgRaebQX9PdSarmdZTq
 * X8nPiKF3JqsqTUYnfDfwpI9P1RgSGg5dlF8JqBmhQAb0TUTyGLTl8P5eoAmFIjN0
 * MV5nbFzO/nG7x0pos5ky4BpVSt3GVJQkCKr2xZ+FCqnai0RvGIXiAn09bQJjrhuq
 * FV+usJuedGqU41phO8WV0eEqJ/K5jH9UEMtmz3El21yf8mYp915SNZBl7oMH1E7O
 * SoAEbYUulnS93SijaeBkRz5pcts3fyy/eDa5XBFdC+5uLt57ClKabIoauBjkLDOn
 * AZFYWH67dEMd/qmL4oQ7EMCKrF5kcXa5lRL2GpOgop35PTFWzH0FKJM4jbLLWsgy
 * /QIDAQAB
 * -----END PUBLIC KEY-----
 */
export function getPublicKey() {
  return Base64.decode(process.env.VUE_APP_JUMP)
}
